import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import ListView from '../../SystemDesign/ListViewV2';
import { useGetAttemptsQuery } from '../../services/SkillsManagement';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';
import RoleChip from '../../SystemDesign/RoleChip';
import DateInTable from '../../SystemDesign/DateInTable';
import { formattedDate } from '../../Utils/Functions';
import ListViewMenuActions from '../../SystemDesign/ListViewMenuActions';
import { MenuItem } from '@mui/material';
import ReviewAllAttempts from './ReviewAllAttempts';
import SearchInput from '../../SystemDesign/SearchInput';
import ViewToggle from '../../SystemDesign/ViewToggle';
import DownloadIcon from '@mui/icons-material/Download';
import ReviewAttemptsPDFExport from './ReviewAttemptsPDFExport';

const QuizEvaluationListAdmin = ({ viewMode = 'table', onChangeViewMode }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  // Local search stays here (as requested)
  const [searchValue, setSearchValue] = React.useState('');

  // Fetch all attempts (admin view). Backend does not support free-text search; we fetch and filter client-side.
  const attemptsArgs = React.useMemo(() => ({ page: 1, limit: 1000 }), []);
  const { data: attemptsData } = useGetAttemptsQuery(attemptsArgs, {
    refetchOnMountOrArgChange: true,
  });

  // Keep attempts slice in sync with server
  React.useEffect(() => {
    const list = attemptsData?.data?.quizAttempts || attemptsData?.quizAttempts;
    if (Array.isArray(list)) {
      dispatch({ type: actions.INITIALIZE_QUIZ_ATTEMPTS, payload: list });
    }
  }, [attemptsData, dispatch]);

  const attemptsFromStore = useSelector((s) => s.quizAttempts);

  const fmtSeconds = React.useCallback((secs) => {
    if (secs == null) return '-';
    const s = Number(secs) || 0;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m > 0) return `${m} min${rs ? ` ${rs} s` : ''}`;
    return `${rs} s`;
  }, []);

  // Group by user and within user by quiz; flatten to rows: (user, quiz)
  const rows = React.useMemo(() => {
    const list = Array.isArray(attemptsFromStore) && attemptsFromStore.length
      ? attemptsFromStore
      : (attemptsData?.data?.quizAttempts || attemptsData?.quizAttempts || []);

    const byUser = new Map();
    for (const a of list) {
      const userId = a.userId || a.user?.id;
      const username = a.user?.username || a.user?.name || `User #${userId}`;
      const u = byUser.get(userId) || { userId, username, quizzes: new Map() };
      const qid = a.quizId;
      const title = a?.quiz?.title || `Quiz #${qid}`;
      const q = u.quizzes.get(qid) || { quizId: qid, title, attempts: [] };
      q.attempts.push(a);
      q.title = title;
      u.quizzes.set(qid, q);
      byUser.set(userId, u);
    }

    // Build flat rows but keep grouping meta to avoid repeating username
    const rows = [];
    for (const u of Array.from(byUser.values()).sort((a, b) => String(a.username).localeCompare(String(b.username)))) {
      const quizzes = Array.from(u.quizzes.values()).sort((a, b) => String(a.title).localeCompare(String(b.title)));
      for (let idx = 0; idx < quizzes.length; idx += 1) {
        const q = quizzes[idx];
        const sorted = [...q.attempts].sort((x, y) => {
          const dx = new Date(x.updatedAt || x.createdAt || x.startTime || 0).getTime();
          const dy = new Date(y.updatedAt || y.createdAt || y.startTime || 0).getTime();
          return dy - dx;
        });
        const last = sorted[0] || {};
        rows.push({
          user: u.username,
          userId: u.userId,
          quizId: q.quizId,
          title: q.title,
          tentative: q.attempts.length,
          score: last.score ?? null,
          timeSpent: last.timeSpent ?? null,
          status: last.status || '-',
          createdAt: last.endTime || last.updatedAt || last.createdAt || last.startTime || null,
          _userFirstRow: idx === 0,
          _userRowCount: quizzes.length,
        });
      }
    }
    return rows;
  }, [attemptsData, attemptsFromStore]);

  // Columns: include first column User, then quiz columns
  const columns = React.useMemo(() => [
    { id: 'user', accessorKey: 'user', header: 'Employee',
      cell: (info) => {
        const row = info.row?.original || {};
        if (!row._userFirstRow) return '';
        return row.user;
      }
    },
    { id: 'title', accessorKey: 'title', header: 'Quiz Title' },
    { id: 'tentative', accessorKey: 'tentative', header: 'Tentative' },
    { id: 'score', accessorKey: 'score', header: 'Score', cell: (info) => info.getValue() ?? '-' },
    { id: 'timeSpent', accessorKey: 'timeSpent', header: 'Time', cell: (info) => fmtSeconds(info.getValue()) },
    { id: 'status', accessorKey: 'status', header: 'Status',
      cell: (info) => {
        const s = String(info.getValue() || '').toLowerCase();
        let label = s;
        let colorOverride = colors.green[700];
        let bgColorOverride = colors.green[100];
        if (s === 'completed') {
          label = 'completed';
        } else if (s === 'in_progress' || s === 'in progress') {
          label = 'in progress';
          colorOverride = colors.Orange[400];
          bgColorOverride = colors.Orange[100];
        } else if (s === 'expired') {
          label = 'expired';
          colorOverride = colors.redAccent[600];
          bgColorOverride = colors.redAccent[100];
        }
        return <RoleChip role={label} colorOverride={colorOverride} bgColorOverride={bgColorOverride} />
      }
    },
    { id: 'createdAt', accessorKey: 'createdAt', header: 'Last attempt at',
      cell: (info) => info.getValue() ? <DateInTable date={formattedDate(new Date(info.getValue()))} /> : '-' },
    { id: 'actions', header: '',
      cell: (info) => {
        const row = info.row?.original || {};
        return (
          <ListViewMenuActions>
            <MenuItem onClick={() => setReportState({ open: true, quizId: row.quizId, userId: row.userId })}>
              View report
            </MenuItem>
            <MenuItem onClick={() => setPdfState({ open: true, quizId: row.quizId, userId: row.userId })}>
              <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
              Download PDF
            </MenuItem>
          </ListViewMenuActions>
        );
      }
    },
  ], [colors, fmtSeconds]);

  // Provide required props for ListViewV2 and emulate server-side pagination
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [queryParams, setQueryParams] = React.useState({ sorting: '', pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 25 }) });
  const [pagination, setPagination] = React.useState({ currentPage: 1, rowsPerPage: 25, total: 0, totalPages: 1 });
  const [pageRows, setPageRows] = React.useState([]);
  const [reportState, setReportState] = React.useState({ open: false, quizId: null, userId: null });
  const [pdfState, setPdfState] = React.useState({ open: false, quizId: null, userId: null });

  // Filter rows by search
  const filteredRows = React.useMemo(() => {
    const term = (searchValue || '').toString().toLowerCase().trim();
    if (!term) return rows;
    return rows.filter((r) => {
      const user = (r.user || '').toString().toLowerCase();
      const title = (r.title || '').toString().toLowerCase();
      const status = (r.status || '').toString().toLowerCase();
      return user.includes(term) || title.includes(term) || status.includes(term);
    });
  }, [rows, searchValue]);

  // Recompute pagination when rows or filters change
  React.useEffect(() => {
    const total = filteredRows.length;
    const rowsPerPage = pagination.rowsPerPage || 25;
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
    const currentPage = Math.min(pagination.currentPage || 1, totalPages);
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    setPagination({ currentPage, rowsPerPage, total, totalPages });
    setPageRows(filteredRows.slice(start, end));
  }, [filteredRows, pagination.currentPage, pagination.rowsPerPage]);

  const applyFilters = React.useCallback((cols, nextPagination, sortingInfo, nextSearchValue) => {
    // We only emulate pagination here; sorting/search are no-ops for now
    const rowsPerPage = Number(nextPagination?.rowsPerPage) || 25;
    const currentPage = Number(nextPagination?.currentPage) || 1;
    setPagination((prev) => ({ ...prev, currentPage, rowsPerPage }));
    if (typeof nextSearchValue === 'string') {
      setSearchValue(nextSearchValue);
    }
    setQueryParams((prev) => ({
      ...prev,
      sorting: sortingInfo ? JSON.stringify(sortingInfo) : '',
      pagination: JSON.stringify({ currentPage, rowsPerPage }),
      // Also persist filters (search) so attemptsArgs can forward to backend
      filters: (() => {
        const term = typeof nextSearchValue === 'string' ? nextSearchValue : searchValue;
        if (!term || String(term).trim() === '') return '';
        const t = String(term).trim();
        return JSON.stringify({
          title: [t],
          quizTitle: [t],
          user: [t],
          username: [t],
          status: [t],
          search: [t],
        });
      })()
    }));
  }, [searchValue]);

  return (
    <Box p={3}>
      {/* Search + View toggle in the same line */}
      <Box sx={{ mb: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ flexGrow: 1 }}>
            <SearchInput
              placeholder="Search by employee, quiz, or status..."
              value={searchValue}
              onChange={setSearchValue}
              onSearch={setSearchValue}
              debounce={400}
            />
          </div>
          <ViewToggle value={viewMode} onChange={(v) => onChangeViewMode && onChangeViewMode(v)} tableLabel="Written" cardsLabel="Oral" label="Evaluation type:" />
        </div>
      </Box>
      <ListView
        columns={columns}
        dispatchData={pageRows}
        tableName="quiz-evaluations-admin"
        // Required props for ListViewV2
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        applyFilters={applyFilters}
        pagination={pagination}
        setQueryParams={setQueryParams}
        queryParams={queryParams}
        existingFilters={false}
        existingPagination={true}
        debouncedSearchValue={searchValue}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        getCellProps={(cell) => {
          // Merge 'User' cells using rowSpan and skip on subsequent rows
          if (cell.column?.id === 'user') {
            const row = cell.row?.original || {};
            if (row._userFirstRow) {
              return { rowSpan: row._userRowCount, sx: { verticalAlign: 'middle' } };
            }
            return { skip: true };
          }
          return {};
        }}
      />
      <ReviewAllAttempts
        open={reportState.open}
        onClose={() => setReportState({ open: false, quizId: null, userId: null })}
        quizId={reportState.quizId}
        userId={reportState.userId}
      />
      <ReviewAttemptsPDFExport
        open={pdfState.open}
        onClose={() => setPdfState({ open: false, quizId: null, userId: null })}
        quizId={pdfState.quizId}
        userId={pdfState.userId}
      />
    </Box>
  );
};

export default QuizEvaluationListAdmin;

